import { useState } from 'react';
import { X, Monitor, PenTool, Loader2, FileText } from 'lucide-react';
import { CardSelectInput } from '@/Components/shared/CardSelectInput';
import { FormInput } from '@/Components/shared/FormInput';
import { PhoneInput } from '@/Components/shared/PhoneInput';

interface DeliveryProofConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (signatureType: 1 | 2, email?: string, phone?: string) => void;
  defaultEmail: string;
  defaultPhone: string;
  isSubmitting: boolean;
}

export function DeliveryProofConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  defaultEmail,
  defaultPhone,
  isSubmitting,
}: DeliveryProofConfirmationModalProps) {
  const [signatureType, setSignatureType] = useState<1 | 2>(1);
  const [email, setEmail] = useState(defaultEmail);
  const [phone, setPhone] = useState(defaultPhone);

  if (!isOpen) return null;

  const isElectronic = signatureType === 1;
  const canConfirm = !isElectronic || (!!email && email.includes('@'));

  const handleConfirm = () => {
    onConfirm(signatureType, isElectronic ? email : undefined, isElectronic ? phone : undefined);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-flex tw-items-center tw-justify-center tw-z-[101] tw-p-4">
      <div className="tw-bg-white tw-rounded-lg tw-shadow-xl tw-max-w-2xl tw-w-full tw-max-h-[80vh] tw-overflow-y-auto">
        {/* Header */}
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-gray-200">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-10 tw-h-10 tw-bg-affinicar-lighter tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <FileText className="tw-w-5 tw-h-5 tw-text-affinicar-primary" />
            </div>
            <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
              Générer l'attestation
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors disabled:tw-opacity-50"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="tw-p-6 tw-space-y-6">
          {/* Signature type selector */}
          <div>
            <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-3">Type de signature</h4>
            <CardSelectInput
              options={[
                { value: '1', label: 'Électronique', description: 'Signature en ligne par le client', icon: <Monitor className="tw-w-5 tw-h-5" /> },
                { value: '2', label: 'Manuscrite', description: 'Signature papier physique', icon: <PenTool className="tw-w-5 tw-h-5" /> },
              ]}
              value={signatureType.toString()}
              onChange={(val) => setSignatureType(parseInt(val, 10) as 1 | 2)}
              name="signature-type"
              required
              columns={2}
            />
          </div>

          {/* Email/phone for electronic signature */}
          {isElectronic && (
            <div className="tw-space-y-4">
              <p className="tw-text-sm tw-text-gray-600">
                La signature électronique sera envoyée aux coordonnées ci-dessous. Vous pouvez les modifier si nécessaire.
              </p>

              <FormInput
                type="email"
                label="Email du bénéficiaire"
                value={email}
                onChange={setEmail}
                placeholder="email@exemple.fr"
                required
              />

              <PhoneInput
                label="Téléphone du bénéficiaire"
                value={phone}
                onChange={setPhone}
                required
              />
            </div>
          )}

          {/* Info for handwritten signature */}
          {!isElectronic && (
            <div className="tw-p-4 tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-text-sm tw-text-amber-800">
              L'attestation sera générée au format PDF. Vous devrez ensuite la faire signer manuellement par le client, puis uploader le document signé depuis la liste des attestations.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="tw-flex tw-justify-end tw-gap-3 tw-p-6 tw-border-t tw-border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !canConfirm}
            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-affinicar-primary tw-rounded-lg hover:tw-bg-affinicar-secondary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
                Génération en cours...
              </>
            ) : (
              "Générer l'attestation"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
